import { PencilIcon, CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Card, CardBody, Button, Textarea, Input, addToast } from '@heroui/react'
import { useState } from 'react'
import { DEFAULT_TEST_PROFILES } from './test_profiles/defaultTestProfiles'
import { toast } from 'react-toastify'

export default function TestProfiles({ 
  onObjectiveChange, 
  customObjectives = {}, 
  onSelectedProfilesChange,
  selectedProfiles,
  customProfiles = [],
  onCustomProfilesChange
}) {

  const [editingObjective, setEditingObjective] = useState(
    Object.fromEntries(DEFAULT_TEST_PROFILES.map(profile => [profile.name, false]))
  )

  const [isAddingProfile, setIsAddingProfile] = useState(false)
  const [newProfileName, setNewProfileName] = useState('')
  const [newProfileObjective, setNewProfileObjective] = useState('')

  const MAX_PROFILES = 5

  const toggleObjectiveEdit = (profileName) => {
    setEditingObjective(prev => ({
      ...prev,
      [profileName]: !prev[profileName]
    }))
  }

  const handleObjectiveChange = (profileName, value) => {
    onObjectiveChange?.(profileName, value)
  }

  const handleProfileToggle = (profileName) => {
    const newSelected = new Set(selectedProfiles)
    if (selectedProfiles.has(profileName)) {
      newSelected.delete(profileName)
    } else {
      if (newSelected.size < MAX_PROFILES) {
        newSelected.add(profileName)
      } else {
        toast.warn('You can only have up to 5 test profiles selected at a time. Unselect a test profile to use a new profile.', {
            position: 'top-left'
        })
      }
    }
    onSelectedProfilesChange?.(newSelected)
  }

  const handleAddProfile = () => {
    if (!newProfileName.trim() || !newProfileObjective.trim()) {
      return
    }

    const newProfile = {
      name: newProfileName.trim(),
      objective: newProfileObjective.trim(),
      isCustom: true
    }

    onCustomProfilesChange([...customProfiles, newProfile])
    
    if (selectedProfiles.size < MAX_PROFILES) {
      handleProfileToggle(newProfile.name)
    }
    
    setNewProfileName('')
    setNewProfileObjective('')
    setIsAddingProfile(false)
  }

  const handleRemoveProfile = (profileName) => {
    onCustomProfilesChange(customProfiles.filter(p => p.name !== profileName))
    if (selectedProfiles.has(profileName)) {
      handleProfileToggle(profileName)
    }
  }

  const allProfiles = [ ...customProfiles, ...DEFAULT_TEST_PROFILES,]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-medium">Test Profiles</h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {selectedProfiles.size} of {MAX_PROFILES} max selected
          </div>
          <Button
            isIconOnly
            variant="light"
            size="sm"
            onPress={() => setIsAddingProfile(true)}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Add custom profile"
          >
            <PlusIcon className="w-5 h-5 text-gray-600" />
          </Button>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Each profile simulates a distinct user scenario to validate different aspects of your pathway. Maximum {MAX_PROFILES} profiles can be selected at once.
      </p>

      {isAddingProfile && (
        <Card className="border-2 border-dashed border-gray-300 bg-gray-50" radius='none'>
          <CardBody className="p-4">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile Name</label>
                <Input
                  placeholder="Enter profile name"
                  value={newProfileName}
                  onChange={e => setNewProfileName(e.target.value)}
                  className="w-full"
                  size="sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objective</label>
                <Textarea
                  placeholder="Enter profile objective"
                  value={newProfileObjective}
                  onChange={e => setNewProfileObjective(e.target.value)}
                  className="w-full"
                  size="sm"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="light"
                  size="sm"
                  onPress={() => {
                    setIsAddingProfile(false)
                    setNewProfileName('')
                    setNewProfileObjective('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  size="sm"
                  onPress={handleAddProfile}
                  disabled={!newProfileName.trim() || !newProfileObjective.trim()}
                >
                  Add Profile
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="space-y-4">
        {allProfiles.map((profile) => {
          const isSelected = selectedProfiles.has(profile.name)
          const currentObjective = customObjectives[profile.name] || profile.objective
          
          return (
            <div 
              key={profile.name}
              onClick={(e) => {
                if (!e.target.closest('button') && 
                    !e.target.closest('textarea') && 
                    !editingObjective[profile.name]) {
                  handleProfileToggle(profile.name)
                }
              }}
            >
              <Card
                className={`transition-[background,border,shadow] duration-75 cursor-pointer ${
                  isSelected 
                    ? 'border-2 border-black bg-gray-100 shadow-sm' 
                    : 'border-gray-200 hover:bg-gray-50'
                } ${editingObjective[profile.name] ? 'cursor-default' : ''}`}
                radius='none'
              >
                <CardBody className="p-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-mono text-base">{profile.name}</h4>
                      {profile.isCustom && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Custom</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.isCustom && (
                        <Button
                          isIconOnly
                          variant="light"
                          size="sm"
                          onPress={() => handleRemoveProfile(profile.name)}
                          className="p-1.5 hover:bg-red-50 rounded-full transition-colors"
                          aria-label="Remove custom profile"
                        >
                          <TrashIcon className="w-5 h-5 text-red-600" />
                        </Button>
                      )}
                      <Button
                        isIconOnly
                        variant="light"
                        size="sm"
                        onPress={() => toggleObjectiveEdit(profile.name)}
                        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label={editingObjective[profile.name] ? "Save changes" : "Edit objective"}
                      >
                        {editingObjective[profile.name] ? (
                          <CheckIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <PencilIcon className="w-3 h-3 text-gray-500" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {editingObjective[profile.name] ? (
                    <Textarea
                      placeholder="Custom Objective"
                      defaultValue={currentObjective}
                      onChange={e => handleObjectiveChange(profile.name, e.target.value)}
                      classNames={{
                        base: "w-full text-sm",
                        input: "min-h-[80px] resize-y"
                      }}
                      size="sm"
                      variant='bordered'
                    />
                  ) : (
                    <p className="text-sm text-gray-600">
                      {currentObjective}
                    </p>
                  )}
                </CardBody>
              </Card>
            </div>
          )
        })}
      </div>
    </div>
  )
}
